import { Bell, Check, X, Calendar, MessageSquare, Activity, Heart, AlertCircle, ArrowLeft, CheckCheck, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { 
  useNotifications, 
  useMarkNotificationRead, 
  useMarkAllNotificationsRead,
  useDeleteNotification 
} from '../../lib/hooks/useNotifications';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common/EmptyState';

interface NotificationsCenterProps {
  onBack?: () => void;
}

export function NotificationsCenter({ onBack }: NotificationsCenterProps) {
  // Fetch notifications from API
  const { data, isLoading, error, refetch } = useNotifications();
  const markAsReadMutation = useMarkNotificationRead();
  const markAllAsReadMutation = useMarkAllNotificationsRead();
  const deleteMutation = useDeleteNotification();

  const notifications = data?.notifications || [];
  const unreadCount = data?.unread_count || 0;

  // Filter notifications by read status
  const unreadNotifications = notifications.filter(n => !n.is_read);
  const readNotifications = notifications.filter(n => n.is_read);

  const handleMarkAsRead = async (id: number) => {
    try {
      await markAsReadMutation.mutateAsync(id);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'appointment':
        return Calendar;
      case 'message':
        return MessageSquare;
      case 'exercise':
      case 'activity':
        return Activity;
      case 'vitals':
      case 'health':
        return Heart;
      case 'alert':
      case 'warning':
        return AlertCircle;
      default:
        return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'appointment':
        return { text: 'text-primary', bg: 'bg-primary/10' };
      case 'message':
        return { text: 'text-[#14CAF4]', bg: 'bg-[#14CAF4]/10' };
      case 'exercise':
      case 'activity':
        return { text: 'text-accent', bg: 'bg-accent/10' };
      case 'vitals':
      case 'health':
        return { text: 'text-green-600', bg: 'bg-green-100' };
      case 'alert':
      case 'warning':
        return { text: 'text-red-600', bg: 'bg-red-100' };
      default:
        return { text: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading notifications..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] p-4">
        <AlertCard
          type="error"
          title="Failed to load notifications"
          description={error?.message || "An error occurred"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div>
              <h1 className="text-primary">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="p-4">
        {notifications.length === 0 ? (
          <Card>
            <CardContent className="p-8">
              <EmptyState
                icon={Bell}
                title="No notifications"
                description="You're all caught up! No new notifications at this time."
              />
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="all">
                All ({notifications.length})
              </TabsTrigger>
              <TabsTrigger value="unread">
                Unread ({unreadCount})
              </TabsTrigger>
              <TabsTrigger value="read">
                Read ({readNotifications.length})
              </TabsTrigger>
            </TabsList>

            {/* All Notifications */}
            <TabsContent value="all">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="space-y-2">
                  {notifications.map((notification) => {
                    const Icon = getNotificationIcon(notification.notification_type);
                    const colors = getNotificationColor(notification.notification_type);

                    return (
                      <Card
                        key={notification.id}
                        className={`transition-colors ${
                          !notification.is_read ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex gap-3">
                            {/* Icon */}
                            <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`w-5 h-5 ${colors.text}`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-medium text-sm">
                                    {notification.title}
                                  </h3>
                                  {notification.priority && notification.priority.toLowerCase() !== 'medium' && (
                                    <Badge variant={getPriorityBadgeColor(notification.priority)} className="text-xs">
                                      {notification.priority}
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatTime(notification.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                {notification.message}
                              </p>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                {!notification.is_read && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleMarkAsRead(notification.id)}
                                    disabled={markAsReadMutation.isPending}
                                    className="h-7 text-xs"
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Mark read
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(notification.id)}
                                  disabled={deleteMutation.isPending}
                                  className="h-7 text-xs text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Unread Notifications */}
            <TabsContent value="unread">
              <ScrollArea className="h-[calc(100vh-200px)]">
                {unreadNotifications.length === 0 ? (
                  <Card>
                    <CardContent className="p-8">
                      <EmptyState
                        icon={CheckCheck}
                        title="No unread notifications"
                        description="You've read all your notifications!"
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {unreadNotifications.map((notification) => {
                      const Icon = getNotificationIcon(notification.notification_type);
                      const colors = getNotificationColor(notification.notification_type);

                      return (
                        <Card key={notification.id} className="bg-blue-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex gap-3">
                              <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h3 className="font-medium text-sm">{notification.title}</h3>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {formatTime(notification.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleMarkAsRead(notification.id)}
                                    className="h-7 text-xs"
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Mark read
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(notification.id)}
                                    className="h-7 text-xs text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Read Notifications */}
            <TabsContent value="read">
              <ScrollArea className="h-[calc(100vh-200px)]">
                {readNotifications.length === 0 ? (
                  <Card>
                    <CardContent className="p-8">
                      <EmptyState
                        icon={Bell}
                        title="No read notifications"
                        description="Read notifications will appear here"
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {readNotifications.map((notification) => {
                      const Icon = getNotificationIcon(notification.notification_type);
                      const colors = getNotificationColor(notification.notification_type);

                      return (
                        <Card key={notification.id}>
                          <CardContent className="p-4">
                            <div className="flex gap-3">
                              <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h3 className="font-medium text-sm">{notification.title}</h3>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {formatTime(notification.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {notification.message}
                                </p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(notification.id)}
                                  className="h-7 text-xs text-red-600"
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
